/// <amd-module name="TFS/Admin/Views/OrganizationOverview/Components/DeleteOrganization/DeleteOrganization" />
import * as React from "react";

import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { IVssPageContext } from "VSS/Platform/Context";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import AdminResources = require("TFS/Admin/Views/OrganizationOverview/Resources");
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { OrganizationOverviewSource } from "TFS/Admin/Views/OrganizationOverview/Sources/OrganizationOverviewSources";
import { DeleteOrganizationDialog } from "Components/DeleteOrganization/DeleteOrganizationDialog";
import { IOrganizationTokenData } from "TFS/Admin/Views/OrganizationOverview/Contracts/DataModels";
import { OrganizationDataProvidersContributions } from "TFS/Admin/Views/OrganizationOverview/Constants/Constants";
import { announce } from "VSS/Core/Util/Accessibility";

export interface IDeleteOrganizationProps {
    pageContext: IVssPageContext;
    organizationId: string;
    organizationName: string;
}

interface IDeleteOrganizationState {
    updateInProgress: boolean;
    error?: string;
    showDeleteOrganizationDialog?: boolean;
}

export class DeleteOrganization extends React.Component<IDeleteOrganizationProps, IDeleteOrganizationState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.props.pageContext);

    constructor(props: IDeleteOrganizationProps) {
        super(props);
        this.state = {
            updateInProgress: false
        };
    }

    public render(): JSX.Element {
        return (
            <div>
                <div className="delete-organization">
                    {this.state.error ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Error}
                            onDismiss={() => this.setState({ error: undefined })}
                        >
                            {this.state.error}
                        </MessageBar>
                    ) : null}
                    <div className="title-m service-title">{AdminResources.DeleteOrganizationHeading}</div>
                    <div className="note">{AdminResources.DeleteOrganizationNote}</div>
                    <div>
                        <Link
                            className="learn-more-link"
                            href={AdminResources.DeleteOrganizationLearnMoreLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={AdminResources.DeleteOrganizationLearnMoreText}
                        >
                            {AdminResources.DeleteOrganizationLearnMoreText}
                        </Link>
                    </div>
                    <Button
                        className="delete-organization-button top-margin"
                        disabled={this.state.updateInProgress}
                        onClick={this._onDeleteOrganizationClick}
                        danger={true}
                    >
                        {AdminResources.DeleteOrganizationButton}
                    </Button>
                    <div className="overview-divider" />
                </div>

                {this.state.showDeleteOrganizationDialog ? (
                    <DeleteOrganizationDialog
                        pageContext={this.context.pageContext}
                        organizationName={this.props.organizationName}
                        onCancel={this._onCancelDelete}
                        onDeleteOrganization={this._onDeleteOrganization}
                    />
                ) : null}
            </div>
        );
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationOverview, Feature.OrganizationOverview, action, properties);
    };

    private _onDeleteOrganizationClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        this._startActionCapture("Show delete organization dialog", {});
        this.setState({ showDeleteOrganizationDialog: true });
    };

    private _onDeleteOrganization = async () => {
        this.setState({ showDeleteOrganizationDialog: false, updateInProgress: true });
        var actionTelemetryLogger = this._startActionCapture("Delete Organization", {});
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        let tokens = await contributionService.getDataAsync<IOrganizationTokenData>(OrganizationDataProvidersContributions.DeleteTokenDataProviderId);
        var source = new OrganizationOverviewSource();
        await source
            .deleteCollection(this.props.organizationId, this.props.pageContext, tokens ? tokens.enterpriseLevelSessionToken : undefined)
            .then(
                result => {
                    if (result) {
                        this.setState({ updateInProgress: false });
                        window.location.href = AdminResources.DeleteOrganizationRouteURL;
                    } else {
                        actionTelemetryLogger.captureError("No result returned from deleteCollection");
                        this.setState({ updateInProgress: false });
                    }
                },
                reason => {
                    actionTelemetryLogger.captureError(reason.message);
                    announce(reason.message);
                    this.setState({ updateInProgress: false, error: reason.message });
                }
            );
    };

    private _onCancelDelete = () => {
        this.setState({ showDeleteOrganizationDialog: false });
    };
}
